<template>
  <div class="admin-view">
    <div class="logo">
      <img src="@/app/assets/images/brand/cb-payments.svg" alt="CB Payments" />
    </div>
    <div class="content-container">
      <div class="admin-header" v-if="!loading">
        <div class="welcome">
          Hi {{ userName }}, Welcome to Chargebee Pay
        </div>
        <button 
          @click="navigateToActions" 
          class="admin-actions-button"
          :disabled="!canExecuteAdminApi || !getApplicationName"
          :class="{ 'disabled-no-app': !getApplicationName }"
          :title="buttonTooltip"
        >
          Admin Actions
        </button>
      </div>
      <div class="context-chooser" v-if="!loading">
        <ChooseApplication v-if="!getApplicationName" />
        <ChooseMerchant :show-close-popup-button="false" v-if="getApplicationName && !getMerchantId" />
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ChooseApplication from "@/components/admin/ChooseApplication";
import ChooseMerchant from "@/components/admin/ChooseMerchant";
import {navigateTo} from "@/router";
import axios from "axios";
import {RouteConstants} from "@/router/routeConstants";
import {getAndStoreMerchantAndConfiguration} from "@/service/sessionService";

export default {
  name: "AdminView",
  components: {ChooseApplication, ChooseMerchant},
  data: () => {
    return {
      merchants: [],
      loading: true,
      userName: "",
      canExecuteAdminApi: false
    };
  },
  computed: {
    ...mapGetters('app', ['getApplicationName', 'getMerchantId', 'getMerchantName', 'isAdminMode']),
    buttonTooltip() {
      if (!this.canExecuteAdminApi) {
        return 'You do not have permission to execute admin actions'
      }
      if (!this.getApplicationName) {
        return 'Please select an application first to access admin actions'
      }
      return 'Execute administrative operations for ' + this.getApplicationName
    }
  },
  async mounted() {
    axios.get("/api/admin/user").then(response => {
      this.$emit('preLoaderStatusMessage', {loadingMessage: this.$t('landing.loggingIn'), keepPreLoaderOpen: false});
      this.userName = response.data.name
      this.canExecuteAdminApi = response.data.canExecuteAdminApi
      this.loading = false;
    }).catch(() => {
      this.$emit('preLoaderStatusMessage', {loadingMessage: this.$t('errors.unAuthorized'), keepPreLoaderOpen: true});
      this.$danger(this.$t('errors.unAuthorized'));
      window.open("/admin/login", "_self")
    })

    this.updateAppState({key: 'adminMode', value: true})
    if (this.getApplicationName && this.getMerchantId) {
      await getAndStoreMerchantAndConfiguration(this.getMerchantId)
      navigateTo(RouteConstants.DASHBOARD, true, {
        adminMode: true,
        applicationName: this.getApplicationName,
        merchantId: this.getMerchantId,
        merchantName: this.getMerchantName
      })
    }
  },
  methods: {
    ...mapActions('app', ['updateAppState']),
    navigateToActions() {
      if (this.canExecuteAdminApi && this.getApplicationName) {
        this.$router.push({ name: RouteConstants.ADMIN_ACTIONS })
      }
    }
  },
}
</script>
<style lang="scss">

.admin-view {
  display: flex;

  & .logo {
    width: 200px;
    padding-top: 40px;
  }

  & .content-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding-top: 40px;

    & .admin-header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 40px;
      margin-bottom: 80px;

      & .welcome {
        font-size: 24px;
        font-weight: $weight_bold;
        flex: 1;
        text-align: center;
      }

      & .admin-actions-button {
        background-color: $primary-600;
        color: white;
        border: none;
        padding: 14px 28px;
        border-radius: 10px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
        
        &:hover:not(:disabled) {
          background-color: $primary-700;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
        }
        
        &:disabled {
          background-color: #D4D4E8;
          color: #9CA3AF;
          cursor: help;
          box-shadow: none;
          transform: none;
          
          &:hover {
            background-color: #E5E7EB;
          }
        }
        
        &.disabled-no-app {
          background-color: #F3F4F6;
          color: #6B7280;
          border: 2px dashed #D1D5DB;
          cursor: help;
          
          &:hover {
            background-color: #F9FAFB;
            border-color: #9CA3AF;
            transform: none;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
        }
      }
    }

    & .context-chooser {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }
}
</style>